import { useEffect, useState } from "react";
import { Button, ButtonToolbar, Form, SelectPicker } from "rsuite";

import "./SongFilters.css";
import { updateIn } from "json-immut";
import { HelperServices } from "../../../core/services/HelperServices";
import { get } from "lodash/fp";
import { SongListParams } from "./SongDataTypes";
import {
  statusObjArr,
  genderObjArr,
  singerCombObjArr,
  mediaTypeObjArr,
  mediaSourceObjArr,
  songTypeObjArr,
} from "../../../core/config/AppConstants";
import { useTranslation } from "react-i18next";

export interface PropTypesInterface {
  onSubmit: Function;
  onClear: Function;
  onlyLicenses?: String;
  hideFilters?: string[];
  showOutMusicFilters?: boolean;
  showHeader?: boolean;
}

export default function SongFilters({
  onSubmit,
  onClear,
  onlyLicenses = null,
  hideFilters = [],
  showOutMusicFilters = false,
  showHeader = true,
}: PropTypesInterface) {
  const { t } = useTranslation();
  const initSearchFormData = {
    language: "",
    language_id: "",
    mood: "",
    mood_id: "",
    name: "",
    singer: "",
    singer_id: "",
    singer_gender: "",
    singer_combination: "",
    actor: "",
    actor_id: "",
    lyricist: "",
    lyricist_id: "",
    composer: "",
    composer_id: "",
    label: "",
    label_id: "",
    genre: "",
    genre_id: "",
    sub_genre: "",
    sub_genre_id: "",
    license: "",
    license_id: "",
    song_type: "",
    media_type: "",
    sourced_from: "",
    media_category: "",
    tempo: "",
    tempo_id: "",
    era: "",
    era_id: "",
    album: "",
    festive_season: "",
    festive_season_id: "",
    release_year: "",
    rating_chart: "",
    rating_chart_id: "",
    status: "",
    smart_search: "",
  };
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<SongListParams>(initSearchFormData);
  const [langList, setLangList] = useState([]);
  const [moodList, setMoodList] = useState([]);
  const [labelList, setLabelList] = useState([]);
  const [genreList, setGenreList] = useState([]);
  const [subGenreList, setSubGenreList] = useState([]);
  const [licenseList, setLicenseList] = useState([]);
  const [tempoList, setTempoList] = useState([]);
  const [eraList, setEraList] = useState([]);
  const [festiveList, setFestiveList] = useState([]);
  const [ratingChartList, setRatingChartList] = useState([]);
  const [showFilter, setShowFilter] = useState(false);
  const handleSearchToggle = () => setShowFilter(!showFilter);

  const isVisible = (filterName: string) => !hideFilters.includes(filterName);

  function handleFormChanges(data: any) {
    let form = updateIn(formData, [data.path], data.value);
    setFormData(form);
  }

  useEffect(() => {
    getLangList({});
    getMoodList({});
    getLabelList({});
    getGenreList({});
    getLicenseList({});
    getTempoList({});
    getEraList({});
    getFestiveList({});
    getRatingChartList({});
  }, []);

  async function getLangList(params: any) {
    try {
      setLoading(true);
      const res = await HelperServices.getAllValidLanguages(params);
      setLangList(get("data.data", res));
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  }

  async function getTempoList(params: any) {
    try {
      setLoading(true);
      const res = await HelperServices.getAllValidTempos(params);
      setTempoList(get("data.data", res));
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  }

  async function getFestiveList(params: any) {
    try {
      setLoading(true);
      const res = await HelperServices.getAllValidFestiveSeasons(params);
      setFestiveList(get("data.data", res));
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  }

  async function getRatingChartList(params: any) {
    try {
      setLoading(true);
      const res = await HelperServices.getAllValidRatingCharts(params);
      setRatingChartList(get("data.data", res));
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  }

  async function getEraList(params: any) {
    try {
      setLoading(true);
      const res = await HelperServices.getAllValidEras(params);
      setEraList(get("data.data", res));
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  }

  async function getLicenseList(params: any) {
    try {
      setLoading(true);
      if (onlyLicenses !== null) {
        params.license = onlyLicenses;
      }
      const res = await HelperServices.getAllValidLicenses(params);
      setLicenseList(get("data.data", res));
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  }

  async function getGenreList(params: any) {
    try {
      setLoading(true);
      const res = await HelperServices.getAllValidGenres(params);
      setGenreList(get("data.data", res));
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  }

  async function getLabelList(params: any) {
    try {
      setLoading(true);
      const res = await HelperServices.getAllValidLabels(params);
      setLabelList(get("data.data", res));
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  }

  async function getMoodList(params: any) {
    try {
      setLoading(true);
      const res = await HelperServices.getAllValidMoods(params);
      setMoodList(get("data.data", res));
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  }

  async function handleGenreChange(genreId: number) {
    try {
      const res = await HelperServices.getAllValidGenres({
        parent_genre: genreId,
      });
      setSubGenreList(res.data.data);
      resetSelectionFor("sub_genre");
      handleFormChanges({ path: "genre_id", value: genreId });
    } catch (error) {
      console.log(error);
    }
  }

  function clearSearch() {
    resetSelectionFor("genre");
    onClear();
    setFormData(initSearchFormData);
  }

  function resetSelectionFor(resetFor: string) {
    let resetData: Object = {};
    switch (resetFor) {
      case "genre":
        resetData = {
          genre_id: "",
          sub_genre_id: "",
        };
        setSubGenreList([]);
        break;
      case "sub_genre":
        resetData = {
          sub_genre_id: "",
        };
        break;
    }
    let newFormData = {
      ...formData,
      ...resetData,
    };
    setFormData(newFormData);
  }

  function renderTextField(
    controlId: string,
    label: string,
    name: keyof SongListParams
  ) {
    return (
      <div className="song-filter-field" key={controlId}>
        <Form.Group controlId={controlId}>
          <Form.ControlLabel>{label}</Form.ControlLabel>
          <Form.Control name={name} />
        </Form.Group>
      </div>
    );
  }

  function renderSelectField({
    controlId,
    label,
    name,
    data,
    valueKey,
    labelKey,
    onSelect,
    onClean,
  }: {
    controlId: string;
    label: string;
    name: keyof SongListParams;
    data: any[];
    valueKey: string;
    labelKey: string;
    onSelect: (value: any) => void;
    onClean?: () => void;
  }) {
    return (
      <div className="song-filter-field" key={controlId}>
        <Form.Group controlId={controlId}>
          <Form.ControlLabel>{label}</Form.ControlLabel>
          <SelectPicker
            data={data}
            valueKey={valueKey}
            labelKey={labelKey}
            onSelect={(value) => {
              onSelect(value);
            }}
            onClean={() => {
              if (onClean) {
                onClean();
                return;
              }
              onSelect("");
            }}
            name={name}
            value={formData?.[name]}
            cleanable
            block
            searchable
            placement="autoVerticalStart"
          />
        </Form.Group>
      </div>
    );
  }

  function renderSection(title: string, fields: JSX.Element[]) {
    if (fields.length === 0) {
      return null;
    }

    return (
      <section className="song-filter-section">
        <div className="song-filter-section-title">{title}</div>
        <div className="song-filter-grid">{fields}</div>
      </section>
    );
  }

  return (
    <div>
      {showHeader && (
        <div className="row mb-2">
          <ButtonToolbar className="mb-hide">
            <Button onClick={handleSearchToggle}>{t("ADVANCE_SEARCH")}</Button>
          </ButtonToolbar>
        </div>
      )}

      {(showFilter || showOutMusicFilters) && (
        <div className="content pb-0 mb-3 song-filter-panel">
          <Form
            formValue={formData}
            onChange={(data: any) => {
              let form = { ...formData, ...data };
              setFormData(form);
            }}
            fluid
            className="song-filter-form"
          >
            {renderSection("Song Details", [
              renderTextField("name", `${t("SONG")} ${t("TITLE")}`, "name"),
              ...(isVisible("language")
                ? [
                    renderSelectField({
                      controlId: "language_id",
                      label: t("LANGUAGE"),
                      name: "language_id",
                      data: langList,
                      valueKey: "id",
                      labelKey: "lang_name",
                      onSelect: (value) =>
                        handleFormChanges({ path: "language_id", value }),
                    }),
                  ]
                : []),
              ...(isVisible("song_type")
                ? [
                    renderSelectField({
                      controlId: "song_type",
                      label: `${t("SONG")} ${t("TYPE")}`,
                      name: "song_type",
                      data: songTypeObjArr,
                      valueKey: "value",
                      labelKey: "label",
                      onSelect: (value) =>
                        handleFormChanges({ path: "song_type", value }),
                    }),
                  ]
                : []),
              ...(isVisible("media_type")
                ? [
                    renderSelectField({
                      controlId: "media_type",
                      label: `${t("MEDIA")} ${t("TYPE")}`,
                      name: "media_type",
                      data: mediaTypeObjArr,
                      valueKey: "value",
                      labelKey: "label",
                      onSelect: (value) =>
                        handleFormChanges({ path: "media_type", value }),
                    }),
                  ]
                : []),
              ...(isVisible("sourced_from")
                ? [
                    renderSelectField({
                      controlId: "sourced_from",
                      label: `${t("SOURCED")} ${t("FROM")}`,
                      name: "sourced_from",
                      data: mediaSourceObjArr,
                      valueKey: "value",
                      labelKey: "label",
                      onSelect: (value) =>
                        handleFormChanges({ path: "sourced_from", value }),
                    }),
                  ]
                : []),
              ...(isVisible("status")
                ? [
                    renderSelectField({
                      controlId: "status",
                      label: t("STATUS"),
                      name: "status",
                      data: statusObjArr,
                      valueKey: "value",
                      labelKey: "label",
                      onSelect: (value) =>
                        handleFormChanges({ path: "status", value }),
                    }),
                  ]
                : []),
            ])}

            {renderSection("People", [
              ...(isVisible("singer")
                ? [
                    renderTextField("singer", t("SINGER"), "singer"),
                    renderSelectField({
                      controlId: "singer_gender",
                      label: `${t("SINGER")} ${t("GENDER")}`,
                      name: "singer_gender",
                      data: genderObjArr,
                      valueKey: "value",
                      labelKey: "label",
                      onSelect: (value) =>
                        handleFormChanges({ path: "singer_gender", value }),
                    }),
                    renderSelectField({
                      controlId: "singer_combination",
                      label: `${t("SINGER")} ${t("COMBINATION")}`,
                      name: "singer_combination",
                      data: singerCombObjArr,
                      valueKey: "value",
                      labelKey: "label",
                      onSelect: (value) =>
                        handleFormChanges({
                          path: "singer_combination",
                          value,
                        }),
                    }),
                  ]
                : []),
              ...(isVisible("actor")
                ? [renderTextField("actor", t("ACTOR"), "actor")]
                : []),
              ...(isVisible("lyricist")
                ? [renderTextField("lyricist", t("LYRICIST"), "lyricist")]
                : []),
              ...(isVisible("composer")
                ? [renderTextField("composer", t("COMPOSER"), "composer")]
                : []),
            ])}

            {renderSection("Classification", [
              ...(isVisible("genre")
                ? [
                    renderSelectField({
                      controlId: "genre_id",
                      label: t("GENRE"),
                      name: "genre_id",
                      data: genreList,
                      valueKey: "id",
                      labelKey: "genre_name",
                      onSelect: (value) => handleGenreChange(value),
                      onClean: () => resetSelectionFor("genre"),
                    }),
                    renderSelectField({
                      controlId: "sub_genre_id",
                      label: t("SUB_GENRE"),
                      name: "sub_genre_id",
                      data: subGenreList,
                      valueKey: "id",
                      labelKey: "genre_name",
                      onSelect: (value) =>
                        handleFormChanges({ path: "sub_genre_id", value }),
                      onClean: () => resetSelectionFor("sub_genre"),
                    }),
                  ]
                : []),
              ...(isVisible("label")
                ? [
                    renderSelectField({
                      controlId: "label_id",
                      label: t("LABEL"),
                      name: "label_id",
                      data: labelList,
                      valueKey: "id",
                      labelKey: "label_name",
                      onSelect: (value) =>
                        handleFormChanges({ path: "label_id", value }),
                    }),
                  ]
                : []),
              ...(isVisible("license")
                ? [
                    renderSelectField({
                      controlId: "license_id",
                      label: t("LICENSE"),
                      name: "license_id",
                      data: licenseList,
                      valueKey: "id",
                      labelKey: "license_name",
                      onSelect: (value) =>
                        handleFormChanges({ path: "license_id", value }),
                    }),
                  ]
                : []),
              ...(isVisible("mood")
                ? [
                    renderSelectField({
                      controlId: "mood_id",
                      label: t("MOOD"),
                      name: "mood_id",
                      data: moodList,
                      valueKey: "id",
                      labelKey: "mood_name",
                      onSelect: (value) =>
                        handleFormChanges({ path: "mood_id", value }),
                    }),
                  ]
                : []),
              ...(isVisible("tempo")
                ? [
                    renderSelectField({
                      controlId: "tempo_id",
                      label: t("TEMPO"),
                      name: "tempo_id",
                      data: tempoList,
                      valueKey: "id",
                      labelKey: "tempo_name",
                      onSelect: (value) =>
                        handleFormChanges({ path: "tempo_id", value }),
                    }),
                  ]
                : []),
            ])}

            {renderSection("Release Info", [
              ...(isVisible("album")
                ? [renderTextField("album", t("ALBUM"), "album")]
                : []),
              ...(isVisible("release_year")
                ? [
                    renderTextField(
                      "release_year",
                      `${t("RELEASE")} ${t("YEAR")}`,
                      "release_year"
                    ),
                  ]
                : []),
              ...(isVisible("era")
                ? [
                    renderSelectField({
                      controlId: "era_id",
                      label: t("ERA"),
                      name: "era_id",
                      data: eraList,
                      valueKey: "id",
                      labelKey: "era_name",
                      onSelect: (value) =>
                        handleFormChanges({ path: "era_id", value }),
                    }),
                  ]
                : []),
              ...(isVisible("festive_season")
                ? [
                    renderSelectField({
                      controlId: "festive_season_id",
                      label: `${t("FESTIVE")} ${t("SEASON")}`,
                      name: "festive_season_id",
                      data: festiveList,
                      valueKey: "id",
                      labelKey: "festive_name",
                      onSelect: (value) =>
                        handleFormChanges({ path: "festive_season_id", value }),
                    }),
                  ]
                : []),
              ...(isVisible("rating_chart")
                ? [
                    renderSelectField({
                      controlId: "rating_chart_id",
                      label: `${t("RATING")} ${t("CHART")}`,
                      name: "rating_chart_id",
                      data: ratingChartList,
                      valueKey: "id",
                      labelKey: "rating_chart_title",
                      onSelect: (value) =>
                        handleFormChanges({ path: "rating_chart_id", value }),
                    }),
                  ]
                : []),
            ])}

            <div className="filter-btn song-filter-actions">
              <Button appearance="subtle" onClick={() => clearSearch()}>
                {t("CLEAR_FILTER")}
              </Button>
              <Button appearance="primary" onClick={() => onSubmit(formData)}>
                {t("SEARCH")}
              </Button>
            </div>
          </Form>
        </div>
      )}
    </div>
  );
}
